// src/pages/HRDigitalHub/HRLayout.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatWidget from '../../components/ChatWidget';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  UserPlus,
  Calendar,
  FileText,
  Bell,
  Search,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Building2,
  UserCheck,
  Settings,
  Camera,
  Kanban,
  FileSpreadsheet,
  Mail,
  MessageSquare,
  ArrowLeft,
  ScrollText,
  ClipboardCheck,
  BarChart2,
  TrendingDown,
  Send,
  Award,
  BarChart3,} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import OnlineUsers from '../../components/OnlineUsers';
import api from '../../api/client';
import useSidebarOrder from '../../hooks/useSidebarOrder';
import { SortableItem, EditOrderButton } from '../../components/SortableSidebar';
import TaskCalendarModal from '../../components/TaskCalendar/TaskCalendarModal';
import { assignedTasksApi, AssignedTask } from '../../api/assignedTasks';
import ExportStatsModal from '../../components/ExportStatsModal';
import TaskKanbanModal from '../../components/TaskKanban/TaskKanbanModal';
import EmailSettingsModal from './components/EmailSettingsModal';
// import MaxSettingsModal from './components/MaxSettingsModal'; // <-- закомментировано (MAX)
import InterviewScriptsModal from './components/InterviewScriptsModal';
import { Tooltip } from 'react-tooltip';
import { useToast } from '../../contexts/ToastContext';
import { notificationsApi, AppNotification } from '../../api/notifications';

interface SearchResult {
  type: string;
  id: string;
  name: string;
  subtitle: string;
  path: string;
}

interface Props {
  children: React.ReactNode;
}

const HRLayout: React.FC<Props> = ({ children }) => {
  const translateAction = (text: string) => {
    if (!text) return '';
    const dict: Record<string, string> = {
      'created': 'создан', 'updated': 'обновлён', 'deleted': 'удалён',
      'cancelled': 'отменено', 'completed': 'завершено', 'rescheduled': 'перенесено',
      'approved': 'утверждено', 'rejected': 'отклонено',
      'added': 'добавлен', 'moved': 'перемещён', 'status_changed': 'статус изменён',
      'interview_scheduled': 'собеседование назначено'
    };
    let translated = text;
    Object.keys(dict).forEach(key => {
      translated = translated.replace(new RegExp(`\\b${key}\\b`, 'gi'), dict[key]);
    });
    return translated;
  };

  

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateUser } = useAuth();
// Используем permissions из AuthContext (единый источник)
  const permissions = (user as any)?.permissions;
  const { showToast } = useToast();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('hr-sidebar-collapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showProfile, setShowProfile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showTaskCalendar, setShowTaskCalendar] = useState(false);
  const [todayTasksCount, setTodayTasksCount] = useState(0);
  const [taskStats, setTaskStats] = useState({ assigneeCritical: 0, creatorCritical: 0 });
  const [showExportModal, setShowExportModal] = useState(false);
  const [showTaskKanban, setShowTaskKanban] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  // const [showMaxSettings, setShowMaxSettings] = useState(false); // <-- закомментировано (MAX)
  const [showInterviewScripts, setShowInterviewScripts] = useState(false);

  const profileRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const backToDashboardButtonRef = useRef<HTMLButtonElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  // ═══════════════════════════════════════════════════════════
  //  УВЕДОМЛЕНИЯ (Notifications)
  // ═══════════════════════════════════════════════════════════
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const lastNotifIdRef = useRef<string | null>(null);
  const navScrollRef = useRef<HTMLElement>(null);

  // ★ Патч hr-scroll-fix: сохранение позиции скролла бокового меню
  useEffect(() => {
    const nav = navScrollRef.current;
    if (!nav) return;
    const KEY = 'hr_nav_scroll_v1';
    const onScroll = () => {
      try { sessionStorage.setItem(KEY, String(nav.scrollTop)); } catch(e) {}
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    return () => nav.removeEventListener('scroll', onScroll);
  }, []);

  // ★ Патч hr-scroll-fix: восстановление скролла при смене маршрута
  useEffect(() => {
    const nav = navScrollRef.current;
    if (!nav) return;
    const KEY = 'hr_nav_scroll_v1';
    try {
      const saved = parseInt(sessionStorage.getItem(KEY) || '0', 10) || 0;
      if (saved > 0 && Math.abs(nav.scrollTop - saved) > 2) {
        nav.scrollTop = saved;
      }
    } catch(e) {}
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('hr-sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  // ─── Polling уведомлений каждые 30 сек ───
  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const loadNotifs = async () => {
      try {
        const [list, count] = await Promise.all([
          // ★ HRM-уведомления (исключаем elearning — он в своём модуле)
          notificationsApi.list({
            module: 'hr',
            types: 'candidate,requisition,vacancy,employee,system,task,meeting',
            limit: 30,
          }),
          notificationsApi.count({
            module: 'hr',
            types: 'candidate,requisition,vacancy,employee,system,task,meeting',
          }).catch(() => 0),
        ]);
        if (!mounted) return;

        const first = list[0];
        const firstId = first?._id || first?.id;
        if (first && !first.read && firstId && firstId !== lastNotifIdRef.current) {
          if (lastNotifIdRef.current !== null) {
            const icon = first.type === 'meeting' ? '🎥' : '🔔';
            showToast(`${icon} ${first.entityName}: ${translateAction(first.action)}`, 'info');
          }
          lastNotifIdRef.current = firstId;
        }

        setNotifications(list);
        setUnreadTotal(count || 0);
      } catch (e) {
        // тихо игнорируем
      }
    };

    loadNotifs();
    // Не дёргаем в фоновой вкладке
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadNotifs();
    }, 30000);
    // Обновляем сразу при возврате на вкладку
    const onVisible = () => { if (document.visibilityState === 'visible') loadNotifs(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      mounted = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, showToast]);

  // ─── Клик вне dropdown ───
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  // ─── Клик по уведомлению ───
  const handleNotifClick = async (n: AppNotification) => {
    const nid = n._id || n.id || '';
    if (!n.read && nid) {
      try { await notificationsApi.markRead(nid); } catch (e) {}
      setNotifications(prev => prev.map(x => (x._id || x.id) === nid ? { ...x, read: true } : x));
      setUnreadTotal(prev => Math.max(0, prev - 1));
    }
    setShowNotifications(false);

    if (n.type === 'meeting' && n.entityId) {
      setShowTaskCalendar(true);
    } else if (n.type === 'candidate' && n.entityId) {
      navigate(`/app/hr/candidates/${n.entityId}`);
    } else if (n.type === 'vacancy' && n.entityId) {
      navigate(`/app/hr/vacancies/${n.entityId}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(x => ({ ...x, read: true })));
      setUnreadTotal(0);
    } catch (e) {}
  };

  const unreadCount = unreadTotal;

  const fetchTodayTasksCount = useCallback(async () => {
    if (!user) return;
    // Проверка: если пользователь без прав на задачи — не грузим
    if (!permissions && user.role !== 'admin' && user.role !== 'superadmin') return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await assignedTasksApi.getTasks({ assigneeId: user.id, dueDate: today });
      const activeTasks = res.data.filter(
        (task: AssignedTask) => !['completed', 'rejected'].includes(task.status)
      );
      setTodayTasksCount(activeTasks.length);
    } catch (err) {
      console.error('Ошибка загрузки количества задач:', err);
    }
  }, [user]);

  const fetchTaskStats = useCallback(async () => {
    if (!user) return;
    if (!permissions && user.role !== 'admin' && user.role !== 'superadmin') return;
    try {
      const res = await assignedTasksApi.getStats();
      setTaskStats(res.data);
    } catch (err) {
      console.error('Ошибка загрузки статистики задач:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user && (permissions || user.role === 'admin' || user.role === 'superadmin')) {
      fetchTodayTasksCount();
      fetchTaskStats();
      const interval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchTodayTasksCount();
          fetchTaskStats();
        }
      }, 30000);
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          fetchTodayTasksCount();
          fetchTaskStats();
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
      };
    }
  }, [user, fetchTodayTasksCount, fetchTaskStats]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      performSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const performSearch = async (query: string) => {
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(query)}`);
      const data: SearchResult[] = res.data;

      const fixedResults = data.map((item) => {
        const { type, id } = item;
        let path = item.path;
        switch (type) {
          case 'candidate':
            path = `/app/hr/candidates/${id}`;
            break;
          case 'employee':
            path = `/app/hr/employees/${id}`;
            break;
          case 'company':
            path = `/app/hr/companies/${id}`;
            break;
          case 'vacancy':
            path = `/app/hr/vacancy/${id}`;
            break;
          case 'requisition':
            path = `/app/hr/requisition/${id}`;
            break;
          default:
            break;
        }
        return { ...item, path };
      });

      const lowerQuery = query.toLowerCase();
      if (
        lowerQuery.includes('движение') ||
        lowerQuery.includes('канбан') ||
        lowerQuery.includes('kanban')
      ) {
        fixedResults.push({
          type: 'navigation',
          id: 'kanban-dashboard',
          name: 'Движение кандидатов',
          subtitle: 'Общий канбан по вакансиям',
          path: '/app/hr/kanban',
        });
      }

      setSearchResults(fixedResults);
      setShowSearchResults(true);
    } catch (err) {
      console.error('Ошибка поиска:', err);
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleResultClick = (path: string) => {
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(path);
  };

  const menuItems = [
    { path: '/app/hr', icon: <LayoutDashboard size={20} />, label: 'Дашборд' },
    { path: '/app/hr/employees', icon: <Users size={20} />, label: 'Сотрудники' },
    { path: '/app/hr/companies', icon: <Building2 size={20} />, label: 'Клиенты' },
    { path: '/app/hr/requisitions', icon: <Briefcase size={20} />, label: 'Заявки на подбор' },
    { path: '/app/hr/vacancies', icon: <UserPlus size={20} />, label: 'Вакансии' },
    { path: '/app/hr/candidates', icon: <UserCheck size={20} />, label: 'Кандидаты' },
    { path: '/app/hr/kanban', icon: <Kanban size={20} />, label: 'Движение кандидатов' },
    { path: '/app/hr/talent-pool', icon: <Users size={20} />, label: 'Кадровый резерв' },
    { path: '/app/hr/checklist-templates', icon: <ClipboardCheck size={20} />, label: 'Чек-листы' },
    { path: '/app/hr/checklist-analytics', icon: <BarChart2 size={20} />, label: 'Аналитика чек-листов' },
    { path: '/app/hr/mass-funnel', icon: <TrendingDown size={20} />, label: 'Воронка масс-подбора' },
    { path: '/app/hr/mass-mailings', icon: <Send size={20} />, label: 'Массовые рассылки' },
    { path: '/app/hr/onboarding', icon: <UserCheck size={20} />, label: 'Доходимость кандидатов' },
    { path: '/app/hr/recruiters-kpi', icon: <Award size={20} />, label: 'KPI рекрутеров' },
    { path: '/app/hr/source-analytics', icon: <BarChart3 size={20} />, label: 'Аналитика источников' },
    { path: '/app/hr/calendar', icon: <Calendar size={20} />, label: 'Календарь отпусков' },
  ];

  // ★ Drag-and-drop порядок пунктов
  const { items: orderedMenuItems, reorder, reset, saving: savingOrder } = useSidebarOrder('hr', menuItems);
  const [editOrder, setEditOrder] = useState(false);

  // Ролевая фильтрация пунктов меню
  const role = user?.role;
   // предполагаем, что AuthContext предоставляет permissions
  // Если permissions не загружены, используем fallback по роли
  const crmSections = permissions?.crmSections || [];
  const allowedLabels: Record<string, string[]> = {
    'dashboard': ['Дашборд'],
    'employees': ['Сотрудники'],
    'clients': ['Клиенты'],
    'requisitions': ['Заявки на подбор'],
    'vacancies': ['Вакансии'],
    'candidates': ['Кандидаты'],
    'kanban': ['Движение кандидатов'],
    'talentPool': ['Кадровый резерв'],
    'vacationCalendar': ['Календарь отпусков'],
  };

      const visibleMenuItems = orderedMenuItems.filter(item => {
    // Полный доступ для админов
    if (role === 'superadmin' || role === 'admin') return true;

    // Дашборд — всегда (пока модуль включён)
    if (item.path === '/app/hr') return true;

    // Пока permissions не загружены — НЕ рендерим остальные пункты (фикс flicker)
    if (!permissions) return false;

    // Используем allowedSections (новый механизм)
    const allowed: string[] = (permissions as any)?.allowedSections || [];

    // Пустой массив = все разделы модуля доступны
    if (allowed.length === 0) return true;

    // Иначе — только выбранные
    return allowed.includes(item.path);
  });

const isActive = (path: string) => {
    if (path === '/app/hr') return location.pathname === '/app/hr';
    return location.pathname.startsWith(path);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && user) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const photoData = reader.result as string;
        try {
          await updateUser({ photo: photoData });
          setShowProfile(false);
          setTimeout(() => setShowProfile(true), 50);
        } catch (err) {
          console.error('Ошибка загрузки фото:', err);
          showToast('Не удалось загрузить фото', 'error');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const panelOffset = 10;
  const sidebarWidth = collapsed ? 80 : 260;
  const buttonLeft = sidebarWidth + panelOffset - 12;

  const getActionButtonStyle = (isActive: boolean = false, color: string = '#2575fc') => ({
    width: '36px',
    height: '36px',
    borderRadius: '18px',
    background: isActive ? `linear-gradient(135deg, #6a11cb, #2575fc)` : 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
    color: isActive ? 'white' : color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
  });

  const actionButtonHover = {
    background: 'linear-gradient(135deg, rgba(106, 17, 203, 0.9), rgba(37, 117, 252, 0.9))',
    color: 'white',
    transform: 'scale(1.02)',
    border: '1px solid rgba(255,255,255,0.6)',
  };

  const taskButtonStyle = {
    ...getActionButtonStyle(false, taskStats.assigneeCritical > 0 || taskStats.creatorCritical > 0 ? 'white' : '#2575fc'),
    backgroundColor: taskStats.assigneeCritical > 0 ? '#ff4d4f' :
                    taskStats.creatorCritical > 0 ? '#faad14' :
                    'rgba(255,255,255,0.2)',
    color: taskStats.assigneeCritical > 0 || taskStats.creatorCritical > 0 ? 'white' : '#2575fc',
  };

  const handleBackButtonMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    Object.assign(e.currentTarget.style, actionButtonHover);
  };
  const handleBackButtonMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
    e.currentTarget.style.color = '#1a1a2e';
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.3)';
  };

  const handleToggleButtonMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    Object.assign(e.currentTarget.style, actionButtonHover);
  };
  const handleToggleButtonMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'white';
    e.currentTarget.style.color = '#2575fc';
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.border = '1px solid #2575fc';
  };

  return (
    <div style={styles.container}>
      <div style={styles.backgroundImage} />
      <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" style={{ display: 'none' }} />
      <div
        style={{
          ...styles.sidebar,
          width: sidebarWidth,
          left: panelOffset,
          top: panelOffset,
          bottom: panelOffset,
          borderRadius: 20,
          boxShadow: '0 20px 40px rgba(0,0,0,0.25), 0 8px 20px rgba(0,0,0,0.15)',
          background: 'rgba(106, 17, 203, 0.2)',
        }}
      >
        <div style={styles.logo}>
          <img src="/images/10.png" alt="" style={styles.logoIcon} />
          {!collapsed && <span style={styles.logoText}> Лама HRM</span>}
        </div>
        {/* ★ Шапка меню: заголовок + кнопка «Порядок» */}
        {!collapsed && (
          <div style={{
            padding: '8px 14px 0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{
              fontSize: 10, color: '#94a3b8', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              {editOrder ? '🖐️ Перетащите' : 'Меню'}
            </span>
            <EditOrderButton
              isEditMode={editOrder}
              onToggle={() => setEditOrder(!editOrder)}
              onReset={reset}
              saving={savingOrder}
            />
          </div>
        )}

        <nav ref={navScrollRef} style={styles.nav}>
          {visibleMenuItems.map((item, idx) => (
            <SortableItem
              key={item.path}
              index={idx}
              total={visibleMenuItems.length}
              onReorder={reorder}
              isEditMode={editOrder && !collapsed}
            >
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="hr-nav-item"
                style={{
                  ...styles.navItem,
                  ...(isActive(item.path) ? styles.navItemActive : {}),
                  justifyContent: collapsed ? 'center' : 'flex-start',
                }}
                data-tooltip-id={collapsed ? "global-tooltip" : undefined}
                data-tooltip-content={collapsed ? item.label : undefined}
                data-tooltip-place="right"
              >
                {item.icon}
                {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
              </button>
            </SortableItem>
          ))}
        </nav>
        <div style={styles.footer}>
          <button
            ref={backToDashboardButtonRef}
            onClick={() => navigate('/dashboard')}
            style={{
              ...styles.backToDashboardButton,
              width: collapsed ? '36px' : '100%',
              height: collapsed ? '36px' : 'auto',
              borderRadius: collapsed ? '18px' : '30px',
              padding: collapsed ? '0' : '10px 16px',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
            data-tooltip-id={collapsed ? "global-tooltip" : undefined}
            data-tooltip-content={collapsed ? "На главный дашборд" : undefined}
            data-tooltip-place="right"
            onMouseEnter={handleBackButtonMouseEnter}
            onMouseLeave={handleBackButtonMouseLeave}
          >
            <ArrowLeft size={16} />
            {!collapsed && <span style={{ marginLeft: '8px' }}>На главный дашборд</span>}
          </button>
        </div>
      </div>

      <button
        ref={toggleButtonRef}
        onClick={() => setCollapsed((prev: boolean) => !prev)}
        style={{ ...styles.toggleButton, left: buttonLeft }}
        onMouseEnter={handleToggleButtonMouseEnter}
        onMouseLeave={handleToggleButtonMouseLeave}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      <div style={{ ...styles.content, marginLeft: sidebarWidth + panelOffset + 20 }}>
        <div style={styles.topbarContainer}>
          <div ref={searchContainerRef} style={styles.searchContainer}>
            <Search size={18} style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Поиск по HRM..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              style={styles.searchInput}
            />
            {showSearchResults && searchResults.length > 0 && (
              <div style={styles.searchResults}>
                {searchResults.map((res) => (
                  <div
                    key={`${res.type}-${res.id}`}
                    style={styles.searchResultItem}
                    onClick={() => handleResultClick(res.path)}
                  >
                    <div style={styles.resultType}>
                      {res.type === 'candidate' ? 'Кандидат' :
                       res.type === 'employee' ? 'Сотрудник' :
                       res.type === 'company' ? 'Компания' :
                       res.type === 'vacancy' ? 'Вакансия' :
                       res.type === 'requisition' ? 'Заявка' : 'Кадровый резерв'}
                    </div>
                    <div style={styles.resultName}>{res.name}</div>
                    <div style={styles.resultSubtitle}>{res.subtitle}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.actions}>
            <OnlineUsers />
            <span data-tooltip-id="global-tooltip" data-tooltip-content="Скрипты собеседований" data-tooltip-place="bottom">
              <button
                style={getActionButtonStyle(false, '#6a11cb')}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, actionButtonHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = '#6a11cb';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onClick={() => setShowInterviewScripts(true)}
              >
                <ScrollText size={20} />
              </button>
            </span>
            <span data-tooltip-id="global-tooltip" data-tooltip-content="Календарь задач" data-tooltip-place="bottom">
              <button
                ref={calendarButtonRef}
                style={taskButtonStyle}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, actionButtonHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = taskStats.assigneeCritical > 0 ? '#ff4d4f' : 
                                                      taskStats.creatorCritical > 0 ? '#faad14' : 
                                                      'rgba(255,255,255,0.2)';
                  e.currentTarget.style.color = taskStats.assigneeCritical > 0 || taskStats.creatorCritical > 0 ? 'white' : '#2575fc';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onClick={() => setShowTaskCalendar(!showTaskCalendar)}
              >
                <Calendar size={20} />
                {todayTasksCount > 0 && <span style={styles.badge}>{todayTasksCount}</span>}
              </button>
            </span>

            {/* Канбан задач временно скрыт */}
            {/* <span data-tooltip-id="global-tooltip" data-tooltip-content="Канбан задач" data-tooltip-place="bottom">
              <button
                style={getActionButtonStyle(false)}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, actionButtonHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = '#2575fc';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onClick={() => setShowTaskKanban(!showTaskKanban)}
              >
                <Kanban size={20} />
              </button>
            </span> */}

            <span data-tooltip-id="global-tooltip" data-tooltip-content="Выгрузка статистики" data-tooltip-place="bottom">
              <button
                ref={exportButtonRef}
                style={getActionButtonStyle(false)}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, actionButtonHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = '#2575fc';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onClick={() => setShowExportModal(true)}
              >
                <FileSpreadsheet size={20} />
              </button>
            </span>

            <div ref={notificationsRef} style={{ position: 'relative', zIndex: 1000 }}>
              <span data-tooltip-id="global-tooltip" data-tooltip-content="Уведомления" data-tooltip-place="bottom">
                <button
                  style={getActionButtonStyle(unreadCount > 0)}
                  onMouseEnter={(e) => Object.assign(e.currentTarget.style, actionButtonHover)}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = unreadCount > 0 ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.color = unreadCount > 0 ? 'white' : '#2575fc';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  onClick={() => setShowNotifications(!showNotifications)}
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span style={styles.badge}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              </span>

              {showNotifications && (
                <div style={styles.notifPopover}>
                  <div style={styles.notifHeader}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                      Уведомления {unreadCount > 0 && <span style={{ color: '#8b5cf6' }}>({unreadCount})</span>}
                    </h4>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} style={styles.notifMarkAll}>
                        Прочитать все
                      </button>
                    )}
                  </div>

                  <div style={styles.notificationsList}>
                    {notifications.length === 0 && (
                      <div style={styles.emptyText}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
                        Нет уведомлений
                      </div>
                    )}
                    {notifications.slice(0, 15).map(n => {
                      const icon = n.type === 'meeting' ? '🎥'
                        : n.type === 'candidate' ? '👤'
                        : n.type === 'vacancy' ? '💼'
                        : n.type === 'task' ? '📋'
                        : n.type === 'system' ? '⚙️'
                        : '🔔';
                      const timeAgo = (() => {
                        const diff = Date.now() - new Date(n.createdAt).getTime();
                        const min = Math.floor(diff / 60000);
                        if (min < 1) return 'только что';
                        if (min < 60) return `${min} мин`;
                        const hr = Math.floor(min / 60);
                        if (hr < 24) return `${hr} ч`;
                        return `${Math.floor(hr / 24)} дн`;
                      })();
                      return (
                        <div
                          key={n._id}
                          onClick={() => handleNotifClick(n)}
                          style={{
                            ...styles.notificationItem,
                            background: n.read ? 'transparent' : 'rgba(139, 92, 246, 0.08)',
                            borderLeft: n.read ? '3px solid transparent' : '3px solid #8b5cf6',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(139, 92, 246, 0.12)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(139, 92, 246, 0.08)'; }}
                        >
                          <div style={styles.notificationIcon}>
                            <span style={{ fontSize: 16 }}>{icon}</span>
                          </div>
                          <div style={styles.notificationContent}>
                            <p style={styles.notificationText}>
                              <strong>{n.entityName}</strong> · {translateAction(n.action)}
                            </p>
                            <p style={styles.notificationTime}>{timeAgo} назад</p>
                          </div>
                          {!n.read && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#8b5cf6', marginTop: 6 }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div ref={profileRef} style={styles.profileWrapper}>
              <span data-tooltip-id="global-tooltip" data-tooltip-content="Профиль" data-tooltip-place="bottom">
                <div style={styles.profile} onClick={() => setShowProfile(!showProfile)}>
                  <div style={styles.avatar}>
                    {user?.photo ? <img src={user.photo} alt={user.name} style={styles.avatarImg} /> : <span>{user?.name?.[0] || 'U'}</span>}
                  </div>
                  {!collapsed && <span style={styles.userName}>{user?.name} {user?.companyName && `(${user.companyName})`}</span>}
                </div>
              </span>
              {showProfile && (
                <div style={styles.popover}>
                  <div style={styles.profileInfo}>
                    <div style={styles.profileAvatarLargeWrapper}>
                      {user?.photo ? (
                        <img src={user.photo} alt={user.name} style={styles.profileAvatarLarge} />
                      ) : (
                        <div style={styles.profileAvatarLarge}>{user?.name?.[0] || 'U'}</div>
                      )}
                      <button style={styles.photoEditButton} onClick={triggerFileInput}><Camera size={16} /></button>
                    </div>
                    <div style={styles.profileDetails}>
                      <h4 style={styles.profileName}>{user?.name || 'Пользователь'}</h4>
                      <p style={styles.profileEmail}>{user?.email || ''}</p>
                      <p style={styles.profileRole}>{user?.role === 'admin' ? 'Администратор' : 'Пользователь'}</p>
                    </div>
                  </div>
                  <div style={styles.profileActions}>
                    <button
                      style={styles.profileActionButton}
                      onClick={() => {
                        setShowProfile(false);
                        setShowEmailSettings(true);
                      }}
                    >
                      <Mail size={16} style={{ marginRight: '8px' }} /> Настройки почты
                    </button>
                    {/* ======= MAX временно убран =======
                    <button
                      style={styles.profileActionButton}
                      onClick={() => {
                        setShowProfile(false);
                        setShowMaxSettings(true);
                      }}
                    >
                      <MessageSquare size={16} style={{ marginRight: '8px' }} /> Настройки MAX
                    </button>
                    ================================== */}
                    <button
                      style={styles.profileActionButton}
                      onClick={() => {
                        setShowProfile(false);
                        logout();
                      }}
                    >
                      <LogOut size={16} style={{ marginRight: '8px' }} /> Выйти
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={styles.pageContent} key={location.pathname}>
          {children}
        </div>
      </div>
      <ChatWidget />
      {showTaskCalendar && (
        <TaskCalendarModal isOpen={showTaskCalendar} onClose={() => setShowTaskCalendar(false)} anchorEl={calendarButtonRef.current} />
      )}
      {showExportModal && user && (
        <ExportStatsModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          companyId={user.companyId || ''}
          companyName={user.companyName || user.company || ''}
        />
      )}
      {showTaskKanban && <TaskKanbanModal isOpen={showTaskKanban} onClose={() => setShowTaskKanban(false)} />}
      {showEmailSettings && <EmailSettingsModal isOpen={showEmailSettings} onClose={() => setShowEmailSettings(false)} />}
      {/* {showMaxSettings && <MaxSettingsModal isOpen={showMaxSettings} onClose={() => setShowMaxSettings(false)} />} */} {/* <-- закомментировано (MAX) */}
      {showInterviewScripts && <InterviewScriptsModal isOpen={showInterviewScripts} onClose={() => setShowInterviewScripts(false)} />}
      <Tooltip id="global-tooltip" delayShow={0} delayHide={100} style={{ zIndex: 2147483647, fontSize: '12px', backgroundColor: 'rgba(0,0,0,0.8)', borderRadius: '8px', padding: '4px 8px' }} />
      
      <style>
        {`
          .hr-nav-item {
            transition: background 0.2s, border-radius 0.2s;
            border-radius: 30px !important;
            margin: 4px 0;
          }
          .hr-nav-item:hover {
            background: rgba(37, 117, 252, 0.1) !important;
          }
          .hr-nav-item:active {
            background: rgba(37, 117, 252, 0.2) !important;
          }
        `}
      </style>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    position: 'relative',
  },
  backgroundImage: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
    backgroundImage: 'url("/images/crm-background2.jpg")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    opacity: 0.3,
  },
  sidebar: {
    position: 'fixed',
    background: 'rgba(106, 17, 203, 0.2)',
    color: 'white',
    transition: 'width 0.3s, left 0.3s, bottom 0.3s, top 0.3s',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '20px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  },
  logoIcon: { height: '32px', width: 'auto' },
  logoText: {
    fontSize: '16px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: '#1a1a2e',
  },
  toggleButton: {
    position: 'fixed',
    top: '60px',
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    background: 'white',
    border: '1px solid #2575fc',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
    transition: 'left 0.3s',
  },
  nav: { flex: 1, padding: '20px 0', overflowY: 'auto' },
  navItem: {
    width: '100%',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '30px',
    margin: '4px 0',
  },
  navItemActive: { background: 'rgba(37, 117, 252, 0.2)', color: '#2575fc', fontWeight: 600, borderRadius: '30px' },
  navLabel: { whiteSpace: 'nowrap' },
  footer: { padding: '20px 16px', borderTop: '1px solid rgba(255,255,255,0.2)' },
  backToDashboardButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: '#1a1a2e',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: 500,
  },
  content: {
    transition: 'margin-left 0.3s',
    minHeight: '100vh',
    paddingTop: '24px',
  
    position: 'relative',
    zIndex: 1,},
  topbarContainer: {
    margin: '0 20px 32px 20px',
    background: 'rgba(255,255,255,0.7)',
    borderRadius: '20px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.3)',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    top: '20px',
  
    position: 'relative',
    zIndex: 9999,},
  searchContainer: {
    position: 'relative',
    width: '300px',
  },
  searchIcon: {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
    zIndex: 1,
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px 8px 36px',
    borderRadius: '30px',
    border: '1px solid rgba(255,255,255,0.3)',
    background: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    outline: 'none',
  },
  searchResults: {
    position: 'absolute',
    top: 'calc(100% + 5px)',
    left: 0,
    right: 0,
    maxHeight: '300px',
    overflowY: 'auto',
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    zIndex: 1002,
  },
  searchResultItem: {
    padding: '10px 12px',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  resultType: {
    fontSize: '10px',
    color: '#2575fc',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  resultName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1a1a2e',
  },
  resultSubtitle: {
    fontSize: '12px',
    color: '#666',
  },
  actions: { display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' },
  badge: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    background: '#ff4d4f',
    color: 'white',
    fontSize: '10px',
    borderRadius: '10px',
    padding: '2px 5px',
    minWidth: '18px',
    textAlign: 'center',
  },
  profileWrapper: { position: 'relative' },
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '30px',
    transition: 'background 0.2s',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    background: 'rgba(37,117,252,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2575fc',
    fontWeight: 600,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  userName: { fontSize: '14px', color: '#333' },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    right: '0',
    width: '320px',
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    zIndex: 1001,
    overflow: 'hidden',
  },
  popoverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.3)',
  },
  popoverTitle: {
    fontSize: '16px',
    fontWeight: 600,
    margin: 0,
    color: '#1a1a2e',
  },
  markAllButton: {
    background: 'transparent',
    border: 'none',
    color: '#2575fc',
    fontSize: '12px',
    cursor: 'pointer',
  },

  notifPopover: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    width: '380px',
    maxWidth: 'calc(100vw - 40px)',
    background: '#ffffff',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
    border: '1px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
    isolation: 'isolate',
    backdropFilter: 'none',
    opacity: 1,
    transform: 'translateZ(0)',
  
    zIndex: 10000,},
  notifHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 18px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    background: '#f8fafc',
  },
  notifMarkAll: {
    background: 'transparent',
    border: 'none',
    color: '#8b5cf6',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
  },

  notificationsList: {
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '10px 0',
  },
  notificationItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px 20px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  },
  notificationIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    background: 'rgba(255,255,255,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  notificationContent: {
    flex: 1,
  },
  notificationText: {
    fontSize: '13px',
    margin: '0 0 4px 0',
    color: '#1a1a2e',
  },
  notificationTime: {
    fontSize: '11px',
    color: '#888',
    margin: 0,
  },
  emptyText: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
  },
  profileInfo: {
    display: 'flex',
    gap: '15px',
    padding: '20px',
    borderBottom: '1px solid rgba(255,255,255,0.3)',
    position: 'relative',
  },
  profileAvatarLargeWrapper: {
    position: 'relative',
    width: '50px',
    height: '50px',
  },
  profileAvatarLarge: {
    width: '50px',
    height: '50px',
    borderRadius: '25px',
    background: 'rgba(37,117,252,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2575fc',
    fontSize: '24px',
    fontWeight: 600,
    overflow: 'hidden',
    objectFit: 'cover',
  },
  photoEditButton: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    width: '20px',
    height: '20px',
    borderRadius: '10px',
    background: '#2575fc',
    border: '2px solid white',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  profileDetails: {
    flex: 1,
  },
  profileName: {
    fontSize: '16px',
    margin: '0 0 4px 0',
    color: '#1a1a2e',
  },
  profileEmail: {
    fontSize: '12px',
    color: '#666',
    margin: '0 0 4px 0',
  },
  profileRole: {
    fontSize: '12px',
    color: '#2575fc',
    margin: 0,
  },
  profileActions: {
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  profileActionButton: {
    width: '100%',
    padding: '10px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#1a1a2e',
    cursor: 'pointer',
    transition: 'background 0.2s',
    textAlign: 'left',
  },
  pageContent: {
    padding: '30px 48px 20px 48px',
    animation: 'fadeIn 0.25s ease-out',
  },
};

export default HRLayout;